import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { Botao, Segmented } from "./Sga";

type Formato = "celular" | "computador";

/** Medidas reais de aparelho, não números redondos: o que trava o layout é a
 *  largura de um iPhone SE (375) e a de um Galaxy comum (390). Prever em 400
 *  esconde exatamente a quebra que aparece na mão do cliente. */
const MEDIDAS: Record<
  Formato,
  { largura: number; altura: number; nota: string }
> = {
  celular: {
    largura: 390,
    altura: 780,
    nota: "390 × 780 · iPhone 14 / Galaxy S22",
  },
  computador: { largura: 1180, altura: 760, nota: "1180 × 760 · notebook" },
};

/**
 * Prévia do link de suporte dentro do painel.
 *
 * Existe porque a pergunta "como isso chega no celular do cliente?" não tinha
 * resposta sem pedir o link no WhatsApp e abrir no próprio aparelho. O iframe
 * carrega a página real — mesma marca, mesma vitrine, mesmo formulário —, e
 * como as media queries olham a largura do iframe, o layout de celular aparece
 * aqui igual ao que o cliente vê.
 *
 * A prévia **não** é uma simulação: é a página, servida pelo container de
 * formulário. Se ela quebrar aqui, quebrou para o cliente.
 */
export function PreviaLink({
  url,
  aoFechar,
}: {
  url: string;
  aoFechar: () => void;
}) {
  const [formato, setFormato] = useState<Formato>("celular");
  const m = MEDIDAS[formato];

  // Esc fecha. Numa camada que cobre a tela inteira, tirar o teclado do
  // operador e deixar só o clique no X é perder tempo dele à toa.
  useEffect(() => {
    const t = (e: KeyboardEvent) => e.key === "Escape" && aoFechar();
    window.addEventListener("keydown", t);
    return () => window.removeEventListener("keydown", t);
  }, [aoFechar]);

  return (
    <div
      className="previa"
      role="dialog"
      aria-modal="true"
      aria-label="Prévia do link de suporte"
    >
      <div className="previa__topo">
        <Segmented
          valor={formato}
          onChange={setFormato}
          opcoes={[
            { valor: "celular", rotulo: "Celular" },
            { valor: "computador", rotulo: "Computador" },
          ]}
        />
        <span className="previa__nota">{m.nota}</span>
        <span style={{ flex: 1 }} />
        <Botao
          icone="mdi:open-in-new"
          onClick={() => window.open(url, "_blank", "noopener")}
        >
          Abrir em aba
        </Botao>
        <Botao icone="mdi:close" onClick={aoFechar}>
          Fechar
        </Botao>
      </div>

      <div className="previa__palco">
        <div
          className={`previa__aparelho previa__aparelho--${formato}`}
          style={{ width: m.largura, height: m.altura }}
        >
          {/* `key` no formato força recarga ao trocar de tamanho: a página mede
              a viewport no carregamento (o ajuste de teclado, entre outros), e
              redimensionar o iframe sem recarregar deixava medida velha. */}
          <iframe
            key={formato}
            src={url}
            title="Prévia da página de suporte"
            className="previa__tela"
            // A página é nossa, mas é pública e recebe conteúdo de terceiros
            // (logo do representante, anexos). Sem `allow-top-navigation` ela
            // não consegue tirar o operador do painel.
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow="microphone"
          />
        </div>
      </div>

      <div className="previa__rodape">
        <Icon icon="mdi:information-outline" width={14} />
        Esta é a página real, não uma simulação — inclusive o cadastro e o envio
        de mensagem funcionam daqui.
      </div>
    </div>
  );
}

/**
 * Botão que abre a prévia. Junto do link, que é onde a dúvida aparece.
 *
 * `compacto` deixa só o ícone: na grade de clientes a coluna de ações já tem
 * copiar, rotacionar e revogar, e um quarto botão com texto empurraria os
 * outros para fora.
 */
export function BotaoPrevia({
  url,
  compacto,
}: {
  url: string;
  compacto?: boolean;
}) {
  const [aberta, setAberta] = useState(false);
  return (
    <>
      <Botao
        icone="mdi:cellphone"
        onClick={() => setAberta(true)}
        title="Ver como fica no celular"
      >
        {compacto ? "" : "Ver no celular"}
      </Botao>
      {aberta && <PreviaLink url={url} aoFechar={() => setAberta(false)} />}
    </>
  );
}
